// @flow

import { createSelector, type OutputSelector } from 'reselect';

import { selectRawProductsArray, type BundleType } from '@gandi/cart';

import { notifyDanger } from '@gandi/notifications';

import type { TranslatorProps } from '@gandi/translate';

import type { Dispatch, FullState, GetState, ThunkAction } from 'src/types';

import type {
  BaseSuggestion,
  Suggestion,
  ExtendedSuggestion,
  Details,
  List,
  ListItem as ResultListItem,
} from '../../types';

import { selectDefaultBundleContent, selectBundleContent } from './bundle';
import { selectFiltersValues } from './filters';

const EMPTY_ARRAY: Array<ExtendedSuggestion> = [];

export const selectRawList = (state: FullState): List =>
  state.domainSuggest.results.list;

export const selectProvided: (state: FullState) => Array<ResultListItem> =
  createSelector([selectRawList], array => array.filter(item => item.provided));
export const selectNonProvided: (state: FullState) => Array<ResultListItem> =
  createSelector([selectRawList], array =>
    array.filter(item => !item.provided),
  );

export const selectProvidedFQDNs: (state: FullState) => Array<string> =
  createSelector([selectProvided], array => array.map(item => item.fqdn));
const selectNonProvidedFQDNs: (state: FullState) => Array<string> =
  createSelector([selectNonProvided], array => array.map(item => item.fqdn));

const selectRawDetails = (state: FullState): Details =>
  state.domainSuggest.results.details;

type ResultsSortByAvailability = {
  available: Array<BaseSuggestion | Suggestion>,
  unavailable: Array<BaseSuggestion | Suggestion>,
};

export const selectProvidedResultsByAvailability: (
  state: FullState,
) => ResultsSortByAvailability = createSelector([selectRawDetails], fqdns =>
  Object.keys(fqdns).reduce(
    (acc: ResultsSortByAvailability, key) => {
      const fqdn = fqdns[key];
      if (fqdn.provided) {
        if (
          ['available', 'lang_required'].includes(fqdn.availability) &&
          !fqdn.error
        ) {
          acc.available.push(fqdn);
        } else {
          acc.unavailable.push(fqdn);
        }
      }
      return acc;
    },
    { available: [], unavailable: [] },
  ),
);

export type SelectOne = (
  state: FullState,
  fqdn: string,
) => ExtendedSuggestion | void;
export const selectOne =
  // $FlowFixMe[incompatible-call]
  createSelector(
    [
      createSelector(
        [selectRawDetails, (_, fqdn) => fqdn],
        (raw, fqdn) => raw[fqdn],
      ),
      createSelector(
        [selectRawProductsArray, (_, fqdn) => fqdn],
        (products, fqdn) =>
          products.find(product => product.payload.fqdn === fqdn),
      ),
      createSelector(
        [selectDefaultBundleContent, (_, fqdn) => fqdn],
        (content, fqdn) => content.includes(fqdn),
      ),
    ],
    (result, product, bundled) => {
      if (!product && !bundled) {
        return result;
      }

      return {
        ...result,
        bundled,
        addedToCart: !!product,
        cartBundleId: product ? product.bundleId : undefined,
      };
    },
  ) as SelectOne;

export function suggestionThunkCallback(
  fqdn: string,
  callback: (suggestion: ExtendedSuggestion) => void,
  __: TranslatorProps['__'],
): ThunkAction<void> {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const currentSuggestion = selectOne(state, fqdn);

    if (currentSuggestion) callback(currentSuggestion);
    else dispatch(notifyDanger(__('Unable to add the products in the cart.')));
  };
}

export const selectAreAllProvided: (state: FullState) => boolean =
  createSelector([selectRawList], array => array.every(item => item.provided));

// FIXME: Refactor this selector so we don't have to call `selectOne` in a loop.
// and properly leverage memoization.
export const selectAllResults = (
  state: FullState,
): Array<ExtendedSuggestion> => {
  const list = selectRawList(state);
  if (!list.length) {
    return EMPTY_ARRAY;
  }
  // $FlowFixMe[incompatible-call]
  return list.map(item => selectOne(state, item.fqdn));
};

export const selectFilteredRawList: (
  state: FullState,
  type: BundleType,
) => Array<ExtendedSuggestion> = createSelector(
  [
    selectAllResults,
    selectDefaultBundleContent,
    selectNonProvidedFQDNs,
    selectFiltersValues,
  ],
  (allResults, bundleContent, nonProvided, filters) => {
    const filterFuncs: Array<(result: ExtendedSuggestion) => boolean> = [
      (result: ExtendedSuggestion) => nonProvided.includes(result.fqdn),
    ]
      .concat(
        // $FlowFixMe[incompatible-type]
        filters.premiums === 'hide' &&
          ((result: ExtendedSuggestion) => !result.premium),
      )
      .concat(
        // $FlowFixMe[incompatible-type]
        filters.premiums === 'show-only' &&
          // $FlowFixMe[incompatible-type]
          ((result: ExtendedSuggestion) => result.premium),
      )
      .concat(
        // $FlowFixMe[incompatible-type]
        filters.unavailable === 'hide' &&
          ((result: ExtendedSuggestion) =>
            !result.availability ||
            result.availability === 'available' ||
            result.availability === 'lang_required'),
      )
      .concat(
        // $FlowFixMe[incompatible-type]
        filters.unavailable === 'show-only' &&
          ((result: ExtendedSuggestion) =>
            result.availability &&
            result.availability !== 'available' &&
            result.availability !== 'lang_required'),
      )
      .filter(Boolean);

    return filterFuncs.reduce(
      (acc, current) => acc.filter(current),
      allResults,
    );
  },
);

export const selectFilteredList: (
  state: FullState,
  type: BundleType,
) => Array<string> = createSelector([selectFilteredRawList], list =>
  list.map(result => result.fqdn),
);

export const selectProvidedList: (
  state: FullState,
  type: BundleType,
) => Array<string> = createSelector(
  [selectProvidedFQDNs, selectDefaultBundleContent],
  (fqdns, bundleContent) => {
    if (!bundleContent.length) {
      return fqdns;
    }

    return fqdns.filter(fqdn => !bundleContent.includes(fqdn));
  },
);

// const selectCountGolive: (state: FullState) => number = createSelector(
//   [selectAllResults],
//   results => results.filter(result => result.phase === 'golive').length
// );

// const selectCountLandrush: (state: FullState) => number = createSelector(
//   [selectAllResults],
//   results => results.filter(result => result.phase === 'landrush').length
// );

// const selectCountSunrise: (state: FullState) => number = createSelector(
//   [selectAllResults],
//   results => results.filter(result => result.phase === 'sunrise').length
// );

export const selectHasCorporateTLDs: (state: FullState) => boolean =
  createSelector(
    [selectAllResults],
    results => results.filter(result => result.corporate).length > 0,
  );

export const selectHasIDNResults: (state: FullState) => boolean =
  createSelector(
    [selectAllResults],
    results =>
      results.filter(
        result => result.allowsLang && result.availability === 'lang_required',
      ).length > 0,
  );

// const selectCountPromotion: (state: FullState) => number = createSelector(
//   [selectAllResults],
//   results =>
//     results.filter(
//       result =>
//         result.availability &&
//         result.prices &&
//         // $FlowFixMe[incompatible-type]
//         // $FlowFixMe[incompatible-use]
//         result.prices.create.hasDiscount
//     ).length
// );

export const selectCountUnavailable: (state: FullState) => number =
  createSelector(
    [selectAllResults],
    results =>
      results.filter(
        result => result.availability && result.availability !== 'available',
      ).length,
  );

// const selectCountAvailable: (state: FullState) => number = createSelector(
//   [selectAllResults],
//   results =>
//     results.filter(
//       result => result.availability && result.availability === 'available'
//     ).length
// );

export const selectCountPremium: (state: FullState) => number = createSelector(
  [selectAllResults],
  results =>
    results.filter(result => result.availability && result.premium).length,
);

export const selectBundledResults: OutputSelector<
  FullState,
  BundleType,
  Array<ExtendedSuggestion>,
> = createSelector(
  [selectBundleContent, selectAllResults],
  (bundleContent, results) => {
    if (!bundleContent.length) {
      return EMPTY_ARRAY;
    }

    return results.filter(suggestion =>
      bundleContent.includes(suggestion.fqdn),
    );
  },
);

export const selectPosition = (state: FullState, fqdn: string): number => {
  const list = selectRawList(state);
  return list.findIndex(suggestion => suggestion.fqdn === fqdn) + 1;
};
